import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy,
  where,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { initializeBoutique, checkIfBoutiqueInitialized } from '../../utils/initBoutique';
import logger from '../../utils/logger';
import { 
  FaStore, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes,
  FaImage,
  FaTshirt,
  FaShoppingCart,
  FaEye,
  FaBoxOpen,
  FaChartLine,
  FaEuroSign,
  FaStar,
  FaTag,
  FaGift,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaShippingFast,
  FaCheck
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminBoutique = () => {
  const [produits, setProduits] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [initializing, setInitializing] = useState(false);
  const [produitForm, setProduitForm] = useState({
    nom: '',
    description: '',
    prix: 0,
    categorie: 'maillot',
    tailles: [],
    stock: 0,
    images: [],
    promotion: 0,
    actif: true,
    rating: 0,
    reviews: 0
  });

  const categories = [
    { value: 'maillot', label: 'Maillots & Textiles', icon: FaTshirt },
    { value: 'accessoire', label: 'Accessoires', icon: FaGift },
    { value: 'supporter', label: 'Articles supporters', icon: FaTag }
  ];

  const taillesDisponibles = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadProduits(), loadCommandes()]);
    } finally {
      setLoading(false);
    }
  };

  const loadProduits = async () => {
    try {
      const q = query(
        collection(db, 'boutique_produits'),
        orderBy('dateCreation', 'desc')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const produitsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dateCreation: doc.data().dateCreation?.toDate(),
          dateModification: doc.data().dateModification?.toDate()
        }));
        setProduits(produitsData);
      });

      return unsubscribe;
    } catch (error) {
      logger.error('Erreur loadProduits:', error);
    }
  };

  const loadCommandes = async () => {
    try {
      const q = query(
        collection(db, 'boutique_commandes'),
        orderBy('dateCommande', 'desc')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const commandesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dateCommande: doc.data().dateCommande?.toDate()
        }));
        setCommandes(commandesData);
      });

      return unsubscribe;
    } catch (error) {
      logger.error('Erreur loadCommandes:', error);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return [];
    
    try {
      setUploading(true);
      const uploadPromises = Array.from(files).map(async (file) => {
        const filename = `boutique/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, filename);
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      });
      
      return await Promise.all(uploadPromises);
    } catch (error) {
      logger.error('Erreur upload images:', error);
      toast.error('Erreur lors de l\'upload des images', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return [];
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!produitForm.nom.trim() || !produitForm.description.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires', {
        icon: '⚠️',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return;
    }

    try {
      const produitData = {
        ...produitForm,
        prix: parseFloat(produitForm.prix),
        stock: parseInt(produitForm.stock),
        promotion: parseInt(produitForm.promotion || 0),
        rating: parseFloat(produitForm.rating || 0),
        reviews: parseInt(produitForm.reviews || 0),
        dateModification: new Date()
      };

      if (editingProduit) {
        await updateDoc(doc(db, 'boutique_produits', editingProduit.id), produitData);
        toast.success('Produit mis à jour avec succès !', {
          icon: '✅',
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      } else {
        produitData.dateCreation = new Date();
        produitData.ventes = 0;
        await addDoc(collection(db, 'boutique_produits'), produitData);
        toast.success('Produit créé avec succès !', {
          icon: '🎉',
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      }

      resetForm();
    } catch (error) {
      logger.error('Erreur handleSubmit:', error);
      toast.error('Erreur lors de la sauvegarde', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const handleEdit = (produit) => {
    setEditingProduit(produit);
    setProduitForm({
      nom: produit.nom,
      description: produit.description,
      prix: produit.prix,
      categorie: produit.categorie,
      tailles: produit.tailles || [],
      stock: produit.stock,
      images: produit.images || [],
      promotion: produit.promotion || 0,
      actif: produit.actif,
      rating: produit.rating || 0,
      reviews: produit.reviews || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (produitId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      return;
    }

    try {
      // Supprimer le produit
      await deleteDoc(doc(db, 'boutique_produits', produitId));
      
      // TODO: Supprimer aussi les images du storage si nécessaire
      toast.success('Produit supprimé avec succès !', {
        icon: '🗑️',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } catch (error) {
      logger.error('Erreur handleDelete:', error);
      toast.error('Erreur lors de la suppression', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const updateCommandeStatus = async (commandeId, newStatus) => {
    try {
      await updateDoc(doc(db, 'boutique_commandes', commandeId), {
        statut: newStatus,
        dateModification: new Date()
      });
      toast.success('Statut de la commande mis à jour !', {
        icon: '📦',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } catch (error) {
      logger.error('Erreur updateCommandeStatus:', error);
      toast.error('Erreur lors de la mise à jour du statut', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const resetForm = () => {
    setProduitForm({
      nom: '',
      description: '',
      prix: 0,
      categorie: 'maillot',
      tailles: [],
      stock: 0,
      images: [],
      promotion: 0,
      actif: true,
      rating: 0,
      reviews: 0
    });
    setEditingProduit(null);
    setShowModal(false);
  };

  const handleTailleToggle = (taille) => {
    setProduitForm(prev => ({
      ...prev,
      tailles: prev.tailles.includes(taille)
        ? prev.tailles.filter(t => t !== taille)
        : [...prev.tailles, taille]
    }));
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-500/20 text-yellow-300';
      case 'confirme': return 'bg-blue-500/20 text-blue-300';
      case 'expedie': return 'bg-purple-500/20 text-purple-300';
      case 'livre': return 'bg-green-500/20 text-green-300';
      case 'annule': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case 'en_attente': return FaClock;
      case 'confirme': return FaCheckCircle;
      case 'expedie': return FaShippingFast;
      case 'livre': return FaCheck;
      case 'annule': return FaTimesCircle;
      default: return FaClock;
    }
  };

  const getStats = () => {
    const totalVentes = commandes.filter(c => c.statut !== 'annule').length;
    const chiffreAffaires = commandes
      .filter(c => c.statut !== 'annule')
      .reduce((total, c) => total + c.total, 0);
    const commandesEnAttente = commandes.filter(c => c.statut === 'en_attente').length;
    const stockFaible = produits.filter(p => p.stock <= 5 && p.stock > 0).length;

    return { totalVentes, chiffreAffaires, commandesEnAttente, stockFaible };
  };

  const handleImageFileChange = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const urls = await handleImageUpload(files);
      setProduitForm(prev => ({
        ...prev,
        images: [...prev.images, ...urls]
      }));
    }
  };

  const removeImage = (index) => {
    setProduitForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleInitializeBoutique = async () => {
    if (!window.confirm('Voulez-vous initialiser la boutique avec des produits de démonstration ?')) {
      return;
    }

    try {
      setInitializing(true);
      const result = await initializeBoutique();
      if (result.success) {
        toast.success(result.message, {
          icon: '✅',
          duration: 4000,
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      } else {
        toast.error(result.message, {
          icon: '❌',
          duration: 4000,
          style: {
            background: '#001B56',
            color: '#FFE400',
          }
        });
      }
    } catch (error) {
      logger.error('Erreur initialisation:', error);
      toast.error('Erreur lors de l\'initialisation', {
        icon: '❌',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } finally {
      setInitializing(false);
    }
  };

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56] p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl shadow-lg">
                <FaStore className="text-3xl text-[#001B56]" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  Administration Boutique
                </h1>
                <p className="text-blue-200 text-lg">
                  Gérez vos produits et suivez vos ventes 📊
                </p>
              </div>
            </div>
            {activeTab === 'produits' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowModal(true)}
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl hover:shadow-xl transition-all duration-300 font-bold text-lg"
              >
                <FaPlus />
                Nouveau Produit
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Navigation Tabs */}
      <div className="flex gap-4 mb-8 overflow-x-auto">
        {[
          { id: 'dashboard', label: 'Tableau de bord', icon: FaChartLine },
          { id: 'produits', label: 'Produits', icon: FaTshirt },
          { id: 'commandes', label: 'Commandes', icon: FaShoppingCart }
        ].map((tab, index) => {
          const IconComponent = tab.icon;
          return (
            <motion.button
              key={tab.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-semibold transition-all duration-300 backdrop-blur-md border whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400] shadow-xl'
                  : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
              }`}
            >
              <IconComponent className="text-xl" />
              <div className="text-left">
                <div>{tab.label}</div>
                {tab.id === 'produits' && (
                  <div className="text-sm opacity-80">{produits.length}</div>
                )}
                {tab.id === 'commandes' && (
                  <div className="text-sm opacity-80">{commandes.length}</div>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Ventes totales',
                  value: stats.totalVentes,
                  icon: FaShoppingCart,
                  color: 'from-blue-500 to-blue-600',
                  suffix: ' articles'
                },
                {
                  title: 'Chiffre d\'affaires',
                  value: stats.chiffreAffaires,
                  icon: FaEuroSign,
                  color: 'from-green-500 to-green-600',
                  suffix: '€'
                },
                {
                  title: 'Commandes en attente',
                  value: stats.commandesEnAttente,
                  icon: FaClock,
                  color: 'from-yellow-500 to-yellow-600'
                },
                {
                  title: 'Stock faible',
                  value: stats.stockFaible,
                  icon: FaBoxOpen,
                  color: 'from-red-500 to-red-600'
                }
              ].map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 bg-gradient-to-r ${stat.color} rounded-xl`}>
                        <IconComponent className="text-2xl text-white" />
                      </div>
                    </div>
                    <h3 className="text-blue-200 text-sm font-medium mb-1">{stat.title}</h3>
                    <p className="text-3xl font-bold text-white">
                      {stat.value}{stat.suffix}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Dernières commandes */}
            <div className="backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20 shadow-xl">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <FaShoppingCart />
                Dernières commandes
              </h2>
              <div className="space-y-4">
                {commandes.slice(0, 5).map((commande, index) => (
                  <motion.div
                    key={commande.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 bg-white/10 rounded-xl border border-white/20"
                  >
                    <div>
                      <h4 className="font-semibold text-white">{commande.userNom}</h4>
                      <p className="text-blue-200 text-sm">
                        {commande.articles.length} article(s) - {commande.total}€
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getStatutColor(commande.statut)}`}>
                      {commande.statut.replace('_', ' ')}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Produits */}
        {activeTab === 'produits' && (
          <motion.div
            key="produits"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {loading ? (
              <div className="text-center py-16">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block rounded-full h-16 w-16 border-4 border-[#FFE400] border-t-transparent mx-auto mb-4"
                />
                <p className="text-xl text-blue-200">Chargement des produits...</p>
              </div>
            ) : produits.length === 0 ? (
              <div className="text-center py-16 backdrop-blur-md bg-white/10 rounded-3xl border border-white/20">
                <FaTshirt className="mx-auto text-8xl text-blue-300 mb-6" />
                <h3 className="text-2xl font-bold text-white mb-3">Aucun produit</h3>
                <p className="text-blue-200 text-lg mb-6">Créez votre premier produit pour commencer !</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-xl font-bold transition-all duration-300"
                  >
                    <FaPlus />
                    Créer un produit
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleInitializeBoutique}
                    disabled={initializing}
                    className="flex items-center gap-3 px-6 py-3 bg-white/20 text-white border border-white/30 rounded-xl font-bold transition-all duration-300 hover:bg-white/30 disabled:opacity-50"
                  >
                    {initializing ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                    ) : (
                      <FaStore />
                    )}
                    {initializing ? 'Initialisation...' : 'Charger produits démo'}
                  </motion.button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {produits.map((produit, index) => (
                  <motion.div
                    key={produit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-xl hover:shadow-2xl transition-shadow"
                  >
                    <div className="aspect-square bg-gray-200 relative">
                      {produit.images?.[0] ? (
                        <img 
                          src={produit.images[0]} 
                          alt={produit.nom}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FFE400]/20 to-[#BDFF00]/20">
                          <FaTshirt className="text-8xl text-[#001B56]/30" />
                        </div>
                      )}
                      {!produit.actif && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white font-bold bg-red-500 px-4 py-2 rounded-xl">INACTIF</span>
                        </div>
                      )}
                      <div className="absolute top-4 right-4 flex gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(produit)}
                          className="p-3 bg-blue-500 text-white rounded-xl shadow-lg hover:bg-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <FaEdit />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(produit.id)}
                          className="p-3 bg-red-500 text-white rounded-xl shadow-lg hover:bg-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </motion.button>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-lg text-white leading-tight">{produit.nom}</h3>
                        {produit.ventes > 0 && (
                          <div className="flex items-center gap-1">
                            <FaStar className="text-[#FFE400] text-sm" />
                            <span className="text-white text-sm font-semibold">{produit.ventes}</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-blue-200 text-sm mb-4 line-clamp-2">{produit.description}</p>
                      
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-200">Prix:</span>
                          <span className="font-bold text-[#FFE400]">{produit.prix}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200">Stock:</span>
                          <span className={`font-bold ${produit.stock > 10 ? 'text-green-400' : produit.stock > 0 ? 'text-orange-400' : 'text-red-400'}`}>
                            {produit.stock}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-200">Catégorie:</span>
                          <span className="font-bold text-white">{categories.find(c => c.value === produit.categorie)?.label}</span>
                        </div>
                        {produit.promotion > 0 && (
                          <div className="flex justify-between">
                            <span className="text-blue-200">Promotion:</span>
                            <span className="font-bold text-red-400">-{produit.promotion}%</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-blue-200">Ventes:</span>
                          <span className="font-bold text-green-400">{produit.ventes || 0}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Commandes */}
        {activeTab === 'commandes' && (
          <motion.div
            key="commandes"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-xl"
          >
            {commandes.length === 0 ? (
              <div className="text-center py-16">
                <FaShoppingCart className="mx-auto text-8xl text-blue-300 mb-6" />
                <h3 className="text-2xl font-bold text-white mb-3">Aucune commande</h3>
                <p className="text-blue-200 text-lg">Les commandes apparaîtront ici</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/20 backdrop-blur-sm">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Commande
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Articles
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {commandes.map((commande, index) => (
                      <motion.tr
                        key={commande.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-white/10 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            #{commande.id.slice(-8)}
                          </div>
                          <div className="text-sm text-blue-200">
                            {commande.dateCommande?.toLocaleDateString?.('fr-FR') || 'Aujourd\'hui'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                          {commande.userNom}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-white">
                            {commande.articles?.length} article(s)
                          </div>
                          <div className="text-sm text-blue-200">
                            {commande.articles?.map(article => article.nom).join(', ')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-[#FFE400]">
                          {commande.total}€
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-sm font-bold rounded-full ${getStatutColor(commande.statut)}`}>
                            {commande.statut?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={commande.statut}
                            onChange={(e) => updateCommandeStatus(commande.id, e.target.value)}
                            className="text-sm bg-white/20 border border-white/30 rounded-xl px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400]"
                          >
                            <option value="en_attente">En attente</option>
                            <option value="confirme">Confirmé</option>
                            <option value="expedie">Expédié</option>
                            <option value="livre">Livré</option>
                            <option value="annule">Annulé</option>
                          </select>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal d'ajout/édition produit */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-3xl font-bold text-[#001B56]">
                    {editingProduit ? 'Modifier le Produit' : 'Nouveau Produit'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="p-3 text-gray-400 hover:text-gray-600 transition-colors rounded-xl hover:bg-gray-100"
                  >
                    <FaTimes />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Nom du produit *
                      </label>
                      <input
                        type="text"
                        value={produitForm.nom}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, nom: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Catégorie
                      </label>
                      <select
                        value={produitForm.categorie}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, categorie: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                      >
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Description *
                    </label>
                    <textarea
                      value={produitForm.description}
                      onChange={(e) => setProduitForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                      rows="4"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Prix (€)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={produitForm.prix}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, prix: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Stock
                      </label>
                      <input
                        type="number"
                        value={produitForm.stock}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, stock: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Promotion (%)
                      </label>
                      <input
                        type="number"
                        value={produitForm.promotion}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, promotion: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Note (0-5)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={produitForm.rating}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, rating: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                        max="5"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Nombre d'avis
                      </label>
                      <input
                        type="number"
                        value={produitForm.reviews}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, reviews: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-3 text-sm font-bold text-gray-700">
                        <input
                          type="checkbox"
                          checked={produitForm.actif}
                          onChange={(e) => setProduitForm(prev => ({ ...prev, actif: e.target.checked }))}
                          className="w-5 h-5 text-[#FFE400] border-2 border-gray-300 rounded focus:ring-[#FFE400]"
                        />
                        Produit actif
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Promotion (%)
                      </label>
                      <input
                        type="number"
                        value={produitForm.promotion}
                        onChange={(e) => setProduitForm(prev => ({ ...prev, promotion: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Tailles disponibles
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {taillesDisponibles.map(taille => (
                        <button
                          key={taille}
                          type="button"
                          onClick={() => handleTailleToggle(taille)}
                          className={`px-4 py-2 rounded-xl border-2 transition-all ${
                            produitForm.tailles.includes(taille)
                              ? 'bg-[#FFE400] text-[#001B56] border-[#FFE400] shadow-lg'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {taille}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">
                      Images
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FFE400] focus:border-[#FFE400] transition-colors"
                    />
                    {produitForm.images.length > 0 && (
                      <div className="flex gap-3 mt-4 flex-wrap">
                        {produitForm.images.map((url, index) => (
                          <div key={index} className="relative">
                            <img 
                              src={url} 
                              alt={`Preview ${index}`}
                              className="w-24 h-24 object-cover rounded-xl border-2 border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-600 shadow-lg"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="produit-actif"
                      checked={produitForm.actif}
                      onChange={(e) => setProduitForm(prev => ({ ...prev, actif: e.target.checked }))}
                      className="rounded border-gray-300 text-[#FFE400] shadow-sm focus:border-[#FFE400] focus:ring focus:ring-[#FFE400] focus:ring-opacity-50 w-5 h-5"
                    />
                    <label htmlFor="produit-actif" className="ml-3 text-gray-700 font-semibold">
                      Produit visible dans la boutique
                    </label>
                  </div>

                  <div className="flex gap-4 pt-8 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors font-semibold"
                    >
                      Annuler
                    </button>
                    <motion.button
                      type="submit"
                      disabled={uploading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl hover:shadow-xl disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-300 font-bold"
                    >
                      <FaSave />
                      {editingProduit ? 'Mettre à jour' : 'Créer'}
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBoutique;